import { Link, useLocation } from "react-router-dom";
import { Home, Plus, Search, User, Menu, Download, Share, Smartphone, Newspaper, Mail, Bell, Shield, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Haptic feedback helper
const triggerHaptic = (pattern = [50]) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

export default function BottomNav({ isLoggedIn = false }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    triggerHaptic([20]);
    setMenuOpen(false);
    
    if (isIOS) {
      // Show iOS instructions dialog
      setShowInstallDialog(true);
    } else if (deferredPrompt) {
      // Trigger Android install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // Show generic instructions if no prompt available
      setShowInstallDialog(true);
    }
  };
  
  const isActive = (path) => location.pathname === path;
  
  // Don't show on certain pages
  const hiddenPaths = ["/register", "/police", "/police/portal", "/admin"];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) {
    return null;
  }

  const navItems = isLoggedIn ? [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/check", icon: Search, label: "Check" },
    { path: "/add-tool", icon: Plus, label: "Add", isMain: true },
    { path: "/profile", icon: User, label: "Profile" },
  ] : [
    { path: "/", icon: Home, label: "Home" },
    { path: "/check", icon: Search, label: "Check" },
    { path: "/login", icon: User, label: "Login" },
  ];

  const handleNavClick = (isMain = false) => {
    if (isMain) {
      triggerHaptic([30, 20, 30]); // FAB tap pattern
    } else {
      triggerHaptic([20]); // Light tap for nav items
    }
  };

  return (
    <>
      {/* Bottom Navigation - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0f172a]/95 backdrop-blur-lg border-t border-yellow-500/20 safe-area-bottom">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            
            if (item.isMain) {
              // Central FAB button for Add Tool
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(true)}
                  className="flex flex-col items-center justify-center -mt-6"
                  data-testid="mobile-add-tool-fab"
                >
                  <div className="w-14 h-14 rounded-full bg-yellow-500 flex items-center justify-center shadow-lg shadow-yellow-500/30 active:scale-95 transition-transform">
                    <Icon className="w-7 h-7 text-[#0f172a]" />
                  </div>
                  <span className="text-[10px] text-yellow-500 mt-1 font-medium">{item.label}</span>
                </Link>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => handleNavClick(false)}
                className={`flex flex-col items-center justify-center min-w-[64px] min-h-[48px] rounded-lg transition-colors ${
                  isActive(item.path)
                    ? "text-yellow-500"
                    : "text-gray-400 active:text-yellow-500/70"
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-[10px] mt-1 font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* Menu button for more options */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                onClick={() => triggerHaptic([20])}
                className="flex flex-col items-center justify-center min-w-[64px] min-h-[48px] rounded-lg text-gray-400 active:text-yellow-500/70 transition-colors"
                data-testid="mobile-nav-menu"
              >
                <Menu className="w-6 h-6" />
                <span className="text-[10px] mt-1 font-medium">More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#0f172a] border-t border-yellow-500/20 rounded-t-2xl">
              <SheetHeader>
                <SheetTitle className="text-white text-left">Menu</SheetTitle>
              </SheetHeader>
              <div className="grid grid-cols-3 gap-4 py-6">
                <Link
                  to="/insurance"
                  onClick={() => { triggerHaptic([20]); setMenuOpen(false); }}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 active:bg-white/10 transition-colors border border-white/10 relative"
                >
                  <ShieldCheck className="w-6 h-6 mb-2 text-emerald-400" />
                  <span className="text-sm text-white">Insurance</span>
                  <Badge className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[8px] px-1.5 py-0.5">
                    Coming Soon
                  </Badge>
                </Link>
                <Link
                  to="/news"
                  onClick={() => { triggerHaptic([20]); setMenuOpen(false); }}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 active:bg-white/10 transition-colors border border-white/10"
                >
                  <Newspaper className="w-6 h-6 mb-2 text-orange-400" />
                  <span className="text-sm text-white">News</span>
                </Link>
                <Link
                  to="/contact"
                  onClick={() => { triggerHaptic([20]); setMenuOpen(false); }}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 active:bg-white/10 transition-colors border border-white/10"
                >
                  <Mail className="w-6 h-6 mb-2 text-green-400" />
                  <span className="text-sm text-white">Contact</span>
                </Link>
                {isLoggedIn && (
                  <Link
                    to="/notifications"
                    onClick={() => { triggerHaptic([20]); setMenuOpen(false); }}
                    className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 active:bg-white/10 transition-colors border border-white/10"
                  >
                    <Bell className="w-6 h-6 mb-2 text-red-400" />
                    <span className="text-sm text-white">Alerts</span>
                  </Link>
                )}
                <Link
                  to="/police"
                  onClick={() => { triggerHaptic([20]); setMenuOpen(false); }}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 active:bg-white/10 transition-colors border border-white/10"
                >
                  <Shield className="w-6 h-6 mb-2 text-blue-400" />
                  <span className="text-sm text-white">Police</span>
                </Link>
                {/* Install App option - only show if not already installed */}
                {!isStandalone && (
                  <button
                    onClick={handleInstallClick}
                    className="flex flex-col items-center justify-center p-4 rounded-xl bg-yellow-500/10 active:bg-yellow-500/20 transition-colors border border-yellow-500/30"
                  >
                    <Smartphone className="w-6 h-6 mb-2 text-yellow-500" />
                    <span className="text-sm text-yellow-500">Install App</span>
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      
      {/* Spacer to prevent content being hidden behind bottom nav */}
      <div className="md:hidden h-20" />

      {/* Install Instructions Dialog (for iOS and fallback) */}
      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="bg-[#0f172a] border-yellow-500/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Smartphone className="w-5 h-5 text-yellow-500" />
              Install TOOLIO
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-300">
            {isIOS ? (
              <>
                <p className="mb-3">To install TOOLIO on your iPhone/iPad:</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li className="flex items-start gap-2">
                    <span>1.</span>
                    <span>Tap the <Share className="w-4 h-4 inline mx-1 text-yellow-500" /> Share button in Safari</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>2.</span>
                    <span>Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>3.</span>
                    <span>Tap <strong className="text-white">"Add"</strong> in the top right</span>
                  </li>
                </ol>
              </>
            ) : (
              <>
                <p className="mb-3">To install TOOLIO on your device:</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>Open this site in Chrome or Edge browser</li>
                  <li>Tap the menu (⋮) button</li>
                  <li>Select <strong className="text-white">"Install app"</strong> or <strong className="text-white">"Add to Home Screen"</strong></li>
                </ol>
              </>
            )}
            <p className="mt-4 text-xs text-gray-500">
              Installing the app gives you quick access from your home screen and works offline.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
